"use client";

import React, { useEffect, useState } from "react";
import AnalyticsSidebar from "../../AnalyticsSidebar";
import { Navbar } from "../../../../components/navbar";
import { Footer } from "../../../../components/footer";
import FormInput from "../../../../components/FormInput";
import styles from "../css/TopSellingMedication.module.css";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { getTopSellingMedications } from "../../../../api/analytics/analyticsApi.js";

export default function TopSellingMedications() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const from = fromDate && toDate ? new Date(fromDate) : undefined;
        const to = fromDate && toDate ? new Date(toDate) : undefined;

        const rows = await getTopSellingMedications({
          fromDate: from,
          toDate: to,
        });
        if (!isMounted) return;

        const chartData = (rows || []).map((row) => ({
          name: row.name,
          value: Number(row.quantitySold ?? 0),
        }));

        setData(chartData);
      } catch (err) {
        if (!isMounted) return;
        setError(err?.message || "Failed to load top selling medications");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    if ((fromDate && toDate) || (!fromDate && !toDate)) {
      load();
    }

    return () => {
      isMounted = false;
    };
  }, [fromDate, toDate]);

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar backTo="/analytics" />

        <div className={`${styles.contentArea} analytics-content`}>
          <AnalyticsSidebar />
          <div className={styles.contentAreaFull}>
            <section className={styles.panelArea}>
              <h2 className={styles.pageTitle}>Top Selling Medications</h2>

              <div className={styles.filtersRow}>
                <div className={styles.inlineFilter}>
                  <span>From</span>
                  <FormInput
                    type="date"
                    name="fromDate"
                    value={fromDate || ""}
                    onChange={(e) => {
                      const value = e.target.value || "";
                      setFromDate(value);
                    }}
                  />
                </div>
                <div className={styles.inlineFilter}>
                  <span>To</span>
                  <FormInput
                    type="date"
                    name="toDate"
                    value={toDate || ""}
                    onChange={(e) => {
                      const value = e.target.value || "";
                      setToDate(value);
                    }}
                  />
                </div>
              </div>

              <div className={styles.chartCard}>
                {error && (
                  <div className="text-red-500 text-sm mb-2">{error}</div>
                )}
                {!loading && !error && (!data || data.length === 0) ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    No data available at the moment
                  </p>
                ) : (
                  <div className={styles.chartInner}>
                    <ResponsiveContainer width="100%" height={360}>
                      <BarChart
                        layout="vertical"
                        data={data}
                        margin={{ top: 28, right: 24, left: 24, bottom: 16 }}
                      >
                        <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                        <XAxis type="number" hide />
                        <YAxis
                          type="category"
                          dataKey="name"
                          tick={{ fontSize: 12 }}
                          width={160}
                        />
                        <Tooltip />
                        <Legend
                          verticalAlign="top"
                          align="center"
                          formatter={() => "Top-Selling Products"}
                        />
                        <Bar dataKey="value" fill="#A78BFA" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}
